/**
 * Messaging shared primitives — channel catalog, brand-tinted channel glyphs,
 * chips, and the message status pill. Verbatim port of the prototype's
 * MSG_CHANNELS / ChGlyph / ChannelChip / MsgTypeChip / msgStatusPill.
 *
 * Design-system only: renders our look on jrni-ui tokens + primitives. No data
 * imports here, so it can be shared by data hooks and screens without cycles.
 */
import { alpha, Pill, T } from "@v0y4ge/jrni-ui";
import type { ReactNode } from "react";
import { useT, t as i18nT } from "../../lib/i18n";

export interface MsgChannel { id: string; name: string; hue: string; glyph: string; }

export const MSG_CHANNELS: MsgChannel[] = [ // ui-config — fixed messaging-channel catalog, tenant-agnostic
  { id: "email", name: "Email", hue: "var(--jrni-color-primary-1, #5142C7)", glyph: "email" },
  { id: "sms", name: "SMS", hue: "#1F8A5B", glyph: "sms" },
  { id: "whatsapp", name: "WhatsApp", hue: "#25D366", glyph: "whatsapp" },
  { id: "calendar", name: "Calendar", hue: "#2D60EB", glyph: "calendar" },
  { id: "wallet", name: "Wallet pass", hue: "#916D0F", glyph: "wallet" },
  { id: "tiktok", name: "TikTok", hue: "#111114", glyph: "tiktok" },
  { id: "instagram", name: "Instagram", hue: "#C13584", glyph: "instagram" },
  { id: "facebook", name: "Facebook", hue: "#1877F2", glyph: "facebook" },
];
export const MSG_CHANNEL = (id: string): MsgChannel => MSG_CHANNELS.find((c) => c.id === id) || MSG_CHANNELS[0];

/** Channels the message BUILDER offers. TikTok, Instagram and Facebook were
 *  retired from the builder on 2026-07-30 (operator: new versions of those come
 *  later). They stay in MSG_CHANNELS so existing rows keep rendering their
 *  chips/glyphs — but no new content can be authored for them. */
export const RETIRED_BUILDER_CHANNELS = ["tiktok", "instagram", "facebook"];
/** Calendar (.ics) and Apple Wallet pass are NOT delivery channels (operator
 *  ruling 2026-09-01, CD prompt §1). They were chips in "Channels · at least
 *  one"; they are now a setting on the email channel with three states — see
 *  artefacts.ts. They stay in MSG_CHANNELS so an existing row's chips and the
 *  delivery log keep rendering, and their authored content (`ics` / `wallet`)
 *  is untouched: only the picker loses them. The channel picker keeps exactly
 *  Email · SMS · WhatsApp. */
export const ARTEFACT_PSEUDO_CHANNELS = ["calendar", "wallet"];
/** No longer any blanket-stub channels (wallet shipped 2026-07-31 — it is now
 *  gated PER MESSAGE via wallet-pass.ts's WALLET_ELIGIBLE, not globally). Kept
 *  as an empty list rather than removed so the two filters below don't need to
 *  change shape the next time a channel ships in two steps. */
export const STUB_BUILDER_CHANNELS: string[] = [];
export const BUILDER_CHANNELS: MsgChannel[] = MSG_CHANNELS.filter((c) => !RETIRED_BUILDER_CHANNELS.includes(c.id) && !ARTEFACT_PSEUDO_CHANNELS.includes(c.id));
/** Channels that carry authored copy — drives the completeness gate. */
export const CONTENT_CHANNELS: MsgChannel[] = BUILDER_CHANNELS.filter((c) => !STUB_BUILDER_CHANNELS.includes(c.id));

/* Compact channel glyph — simple iconographic marks, brand-tinted. */
export function ChGlyph({ id, size = 14, color = "currentColor" }: { id: string; size?: number; color?: string }) {
  // The colour rides on CSS `color` and the stroke reads currentColor, so a CSS variable (the email channel's
  // brand colour) resolves in every browser; a var() in the stroke attribute itself does not resolve everywhere.
  const st = { width: size, height: size, flexShrink: 0, color } as const;
  const P = (inner: ReactNode) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={st}>{inner}</svg>
  );
  switch (id) {
    case "email": return P(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>);
    case "calendar": return P(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>);
    case "wallet": return P(<><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 11h18M16 15h2" /></>);
    case "sms": return P(<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>);
    case "whatsapp": return P(<><path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 21l2-5.5A8.5 8.5 0 1 1 21 11.5z" /><path d="M8.5 9.5c0 4 2 6 6 6" /></>);
    case "tiktok": return P(<><path d="M15 4c.5 2.5 2 4 4.5 4.2" /><path d="M15 4v9.5a4 4 0 1 1-3.2-3.9" /></>);
    case "instagram": return P(<><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="3.5" /><circle cx="17.5" cy="6.5" r="0.5" fill={color} /></>);
    case "facebook": return P(<><path d="M14 8h2V5h-2a3 3 0 0 0-3 3v2H9v3h2v6h3v-6h2.2l.8-3H14V8.5c0-.4.3-.5.6-.5z" /></>);
    default: return P(<circle cx="12" cy="12" r="9" />);
  }
}

export function ChannelChip({ id, size = "md" }: { id: string; size?: "sm" | "md" }) {
  const c = MSG_CHANNEL(id);
  const pad = size === "sm" ? "2px 7px 2px 6px" : "3px 9px 3px 7px";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: pad, borderRadius: 999, background: alpha(c.hue, "14"), color: c.hue, border: `1px solid ${alpha(c.hue, "33")}`, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>
      <ChGlyph id={id} size={12} color={c.hue} /> {c.name}
    </span>
  );
}

/* Message-type chip — distinguishes a Survey-type message from a standard one. */
export function MsgTypeChip({ kind }: { kind: string }) {
  const t = useT();
  if (kind !== "survey") return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px 2px 7px", borderRadius: 999, background: T.p4, border: `1px solid ${T.p3}`, fontSize: 11.5, fontWeight: 700, color: T.p1, whiteSpace: "nowrap", flexShrink: 0 }}>
      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3" width="10" height="15" rx="2" /><path d="M8 3h4v2H8z" /><path d="M8 9h4M8 13h3" /></svg> {t("messaging.shared.survey", "Survey")}</span>
  );
}

/* Two statuses only (operator, 2026-08-04): draft or live. Anything not literally
   "draft" reads as live — same rule as useMessaging.normStatus. */
export const msgStatusPill = (s: string) =>
  s === "draft" ? <Pill>{i18nT("common.status.draft", "Draft")}</Pill>
    : <Pill tone="success" dot>{i18nT("common.status.live", "Live")}</Pill>;

/* ── Appointment-lineage trigger labels (CC 2026-09-03) ────────────────────
 * `appointment.confirmed` → "Appointment confirmed". The key is the contract's;
 * surfaces show the reading AND the key, because an operator searches for
 * "confirmation" and an engineer pastes the key.
 *
 * DIRECTION IS A PROPERTY OF THE TRIGGER, NOT OF A SIGN. `offsetMinutes` is a
 * positive lead time; the compiler's anchor decides which way it counts. The
 * anchor table (CC 2026-09-03), mirrored:
 *
 *   appointment.confirmed        no offset        on the event
 *   appointment.reminder         positive mins    before the appointment
 *   appointment.follow_up        positive mins    after the appointment
 *   any other appointment.*      no offset        on the event
 *
 * A trigger that carries an offset has exactly one direction, so a trigger
 * absent from the map should never arrive WITH an offset — if one does, it gets
 * the magnitude and no direction word rather than a guess. */
const OFFSET_DIRECTION: Record<string, "before" | "after"> = {
  "appointment.reminder": "before",
  "appointment.follow_up": "after",
};

export const triggerLabel = (key: string): string => {
  const tail = String(key ?? "").replace(/^appointment\./, "").replace(/[._-]+/g, " ").trim();
  return tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : key;
};

/** "24 h before" · "2 days after" · "30 min" (direction unknown). */
export const offsetLabel = (trigger: string, mins: number): string => {
  const a = Math.abs(mins);
  const magnitude = a % 1440 === 0 ? `${a / 1440} ${a / 1440 === 1 ? i18nT("common.units.day", "day") : i18nT("common.units.days", "days")}`
    : a % 60 === 0 ? `${a / 60} h`
      : `${a} min`;
  const dir = OFFSET_DIRECTION[trigger];
  return dir ? `${magnitude} ${dir === "before" ? i18nT("messaging.offset.before", "before") : i18nT("messaging.offset.after", "after")}` : magnitude;
};

/** The whole reading: "Reminder · 24 h before". */
export const triggerReading = (trigger: string, mins?: number | null): string =>
  mins ? `${triggerLabel(trigger)} · ${offsetLabel(trigger, mins)}` : triggerLabel(trigger);
